import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { formatUsd, useCostDisplay } from "../lib/cost.ts";

interface Estimate {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  /** Past calls the answer length is averaged from; 0 = a default guess. */
  basedOn: number;
}

/**
 * "About $0.03" next to an AI button, shown before it is clicked. Renders
 * nothing unless the cost display is switched on in Admin, Settings. The
 * figure comes from the server, which knows how much text and which images
 * would be sent.
 */
export function CostEstimate({
  path,
  body = {},
  compact = false,
  className = "",
}: {
  /** API path of the estimate endpoint (POST). */
  path: string;
  body?: unknown;
  /** Short form ("about $0.03") for tight spots such as toolbars. */
  compact?: boolean;
  className?: string;
}) {
  const display = useCostDisplay();
  const enabled = !!display?.enabled;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cost-estimate", path, JSON.stringify(body)],
    queryFn: () => api.post<Estimate>(path, body),
    enabled,
    staleTime: 60_000,
  });

  if (!enabled) return null;
  if (isLoading) return <span className={`text-[11px] text-muted-foreground ${className}`}>Estimating cost…</span>;
  if (isError || !data) return null;

  const detail =
    `About ${data.inputTokens.toLocaleString()} input and ${data.outputTokens.toLocaleString()} output tokens ` +
    `at $${display.inputPerMTok} / $${display.outputPerMTok} per million. ` +
    (data.basedOn > 0
      ? `The answer length is the average of the last ${data.basedOn} similar AI calls.`
      : "The answer length is a typical guess until there are a few past calls to average.");

  return (
    <span className={`text-[11px] text-muted-foreground whitespace-nowrap ${className}`} title={detail}>
      {compact ? `about ${formatUsd(data.costUsd)}` : `Estimated cost: about ${formatUsd(data.costUsd)}`}
    </span>
  );
}
